"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

export type Palette = "classic" | "cobalt";

export const PALETTE_STORAGE_KEY = "ws-palette";
const PALETTES: Palette[] = ["classic", "cobalt"];

function isPalette(value: unknown): value is Palette {
  return value === "classic" || value === "cobalt";
}

/**
 * Resolve the palette the pre-hydration script already committed to <html>.
 * The inline script in `app/layout.tsx` runs before paint and sets
 * `data-palette`, so reading it back here keeps SSR and the client in sync and
 * avoids a flash. Falls back to localStorage, then to the new-user default.
 */
function readInitialPalette(): Palette {
  if (typeof document !== "undefined") {
    const attr = document.documentElement.dataset.palette;
    if (isPalette(attr)) return attr;
  }
  if (typeof localStorage !== "undefined") {
    try {
      const stored = localStorage.getItem(PALETTE_STORAGE_KEY);
      if (isPalette(stored)) return stored;
    } catch {
      /* storage blocked — fall through */
    }
  }
  return "cobalt";
}

type PaletteContextValue = {
  palette: Palette;
  setPalette: (palette: Palette) => void;
  togglePalette: () => void;
  palettes: Palette[];
};

const PaletteContext = createContext<PaletteContextValue | null>(null);

export function PaletteProvider({ children }: { children: ReactNode }) {
  const [palette, setPaletteState] = useState<Palette>(readInitialPalette);

  // Keep the <html data-palette> attribute aligned with React state. The
  // pre-hydration script seeds it; this guards against drift after hydration.
  useEffect(() => {
    if (document.documentElement.dataset.palette !== palette) {
      document.documentElement.dataset.palette = palette;
    }
  }, [palette]);

  // Reflect palette changes made in other tabs/windows.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === PALETTE_STORAGE_KEY && isPalette(event.newValue)) {
        setPaletteState(event.newValue);
        document.documentElement.dataset.palette = event.newValue;
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setPalette = useCallback((next: Palette) => {
    setPaletteState(next);
    document.documentElement.dataset.palette = next;
    try {
      localStorage.setItem(PALETTE_STORAGE_KEY, next);
    } catch {
      /* storage blocked — attribute still applied for this session */
    }
  }, []);

  const togglePalette = useCallback(() => {
    setPalette(palette === "cobalt" ? "classic" : "cobalt");
  }, [palette, setPalette]);

  return (
    <PaletteContext.Provider value={{ palette, setPalette, togglePalette, palettes: PALETTES }}>
      {children}
    </PaletteContext.Provider>
  );
}

export function usePalette(): PaletteContextValue {
  const ctx = useContext(PaletteContext);
  if (!ctx) {
    throw new Error("usePalette must be used within a PaletteProvider");
  }
  return ctx;
}
